// The Dredger harvesting looks of the map (R1120, R1121,
// `ui-map-interface.md` §6.2.1).
//
// A Dredger on a deposit and a Dredger adrift read the same before today.
// The `gather` field of a World theme names one of ten looks, and this layer
// draws the picked look on every hull the viewer may read as cutting.
//
// **Three passes, three slots.** A look draws through three hooks, and each
// hook is its own slot, because the engine flushes one layer whole before the
// next. `gatherunder` stands before `sites`, so an under-hull mark lies below
// the deposit diamond. `gathermid` stands after `holdeffects`, so a mark on
// the field lies over the diamond and under every wake and hull.
// `gatherover` stands after `fleets`, so a mark on the hull lies over its own
// silhouette. ONE class fills all three, the way `EffectLayer` fills two.
//
// **What the layer decides and what it does not.** `GatherState` decides who
// cuts and holds every beat (R1121); `render/gather-styles.ts` resolves the
// theme's look id. This layer owns the clock, the sinks, the cull, the atlas
// and the two suppression sets:
//
//   - `ladenSuppressed` names the hulls whose laden ring the picked look
//     draws itself (`hold-ring`, card 11).
//   - `drawnFields` names the deposits whose mark the picked look draws
//     itself. No shipped look asks for it (R1124): `drain-pulse` draws OVER
//     the picked mark, because the mark is the reader's own Marks pick.
//
// The CARGO LANE is on neither list. The live lane is always on (R1122), so
// no look may take it away and the layer holds no set for it.
//
// **The layer reads no reduced-motion switch, and that is deliberate.** A cut
// is work in progress, not a standing decoration: a frozen bucket chain would
// say "this hull is at work" for as long as the hull sat there, which is what
// the mark is for. The marks are slow — one lot is `lotSec` of wall time —
// so there is no fast motion to take away.
//
// **§10.** Every cut comes out of a pool that is allocated once and never
// grows; the cap is `GATHER_LIMITS.maxCuts`. A cut outside the padded
// viewport packs nothing. A hidden document packs nothing. The context is one
// reused object, and no frame allocates (R131).

import { GATHER_LIMITS, FLEET_CARGO_COLOR } from '../../gen/presentation';
import { depositChips, depositInk } from '../../render/marks';
import {
  AUTO_SCENE_LOOK, type SceneLook,
} from '../../render/scene-look';
import {
  GATHER_AUTO, gatherLook, lighten,
  type GatherCtx, type GatherHook, type GatherLook,
} from '../../render/gather-styles';
import {
  GatherState,
  type GatherCut, type GatherField, type GatherHull,
} from '../../render/gather-state';
import { F_ADD, outside, type DrawCtx, type FrameInfo, type Layer } from '../layer';
import type {
  BatchSource, CircleSink, QuadSink, SegmentSink,
} from './batches';
import { zoomOf } from './shared';
import { hiddenDocument } from '../../render/motion';

/** Where the layer gets what it cannot hold itself. */
export interface GatherFeeds {
  /**
   * Every fleet the viewer may read a MISSION for, once a frame (R1121).
   *
   * That is an own hull and a hull of the own faction, and nothing else: a
   * contact carries no mission, so a contact never reaches this walk. The
   * caller writes into ONE reused record and the layer copies what it needs,
   * so the walk allocates nothing.
   */
  hulls(fn: (h: GatherHull) => void): void;
  /**
   * The deposit inside `radius` of a point, or null.
   *
   * It is the site layer's own "which field is at this point" walk. The
   * answer is a reused record, so the layer reads it inside the call.
   */
  field(x: number, y: number, radius: number): GatherField | null;
}

/** The three hooks, in the order of the three slots. */
const HOOKS = ['under', 'mid', 'over'] as const;
type HookName = (typeof HOOKS)[number];

/** The slot each hook fills. */
export const GATHER_SLOTS: Readonly<Record<HookName, string>> = {
  under: 'gatherunder',
  mid: 'gathermid',
  over: 'gatherover',
};

/** How many instances each sink holds before the engine grows it. */
const QUAD_CAPACITY = 512;
const SEGMENT_CAPACITY = 512;
const CIRCLE_CAPACITY = 256;

/** The drift of the silt and the spoil, as a unit vector. It is one bearing
 *  for the whole map, because the wire carries no current (`gather.toml`). */
const CURRENT_RAD = (GATHER_LIMITS.currentDeg * Math.PI) / 180;
const CURRENT_X = Math.cos(CURRENT_RAD);
const CURRENT_Y = Math.sin(CURRENT_RAD);

/**
 * THE CONTROLLER.
 *
 * It owns the state, the look and the three passes. The scene builds one and
 * registers `passes` into the three slots.
 */
export class GatherLayers {
  /** The one tracker every reader shares (R1121). */
  readonly state = new GatherState();

  /** The three passes, in slot order. */
  readonly passes: readonly GatherPass[];

  private look: SceneLook = AUTO_SCENE_LOOK;
  /** The `#gatherlook=<id>` knob. '' is no override. */
  private forced = '';
  private on = true;

  /** The layer clock in seconds of WALL time. */
  private t = 0;

  /** The frame this advance already ran for, so the first VISIBLE pass
   *  advances the state and the other two read it (`frame.ts` skips the
   *  update of a hidden layer). */
  private stamp = -1;

  /** The hulls and the fields the picked look draws itself. */
  private readonly ladenOff = new Set<number>();
  private readonly fieldsOff = new Set<number>();

  /**
   * Which cut speaks for each field this frame, by site id (R1124).
   *
   * A look that draws on the FIELD draws once per field and not once per
   * hull, so a stack of Dredgers on one deposit gives one mark. The lowest
   * fleet id leads, which holds while the stack stands.
   */
  private readonly leadBySite = new Map<number, number>();

  /** The cull box of this frame, and the camera scale. */
  private x0 = 0; private y0 = 0; private x1 = 0; private y1 = 0;
  private zoom = 1;
  private drawing = true;

  /** The resolved look of this frame, so a pass looks nothing up. */
  private resolved: GatherLook = gatherLook(GATHER_AUTO);

  /** The one reused context (R131). */
  private readonly ctx: GatherCtx;

  /** The commodity inks, built once a commodity rather than once a frame. */
  private readonly inks = new Map<number, { res: number; hi: number; chips: number }>();

  constructor(src: BatchSource, private readonly feeds: GatherFeeds) {
    const sprite = (key: string) => src.sprite(key);
    // ONE SINK TRIPLE PER PASS. The engine runs every layer's `update` before
    // any `draw`, so three passes over one triple would each wipe the one
    // before them.
    this.passes = HOOKS.map((h) => new GatherPass(
      this, h,
      src.newQuads(QUAD_CAPACITY),
      src.newSegments(SEGMENT_CAPACITY),
      src.newCircles(CIRCLE_CAPACITY),
    ));
    const first = this.passes[0];
    this.ctx = {
      t: 0, z: 1, row: this.resolved.row, alpha: 1, add: F_ADD,
      fx: 0, fy: 0, hx: 0, hy: 0, head: 0, restHead: 0,
      res: 0xffffff, resHi: 0xffffff, chips: 1, cargo: FLEET_CARGO_COLOR,
      ink: 0xffffff, deposit: AUTO_SCENE_LOOK.deposit, fieldLead: true,
      work: 0, rate: 1, shaft: false, tw: 0, lot: 0, lotN: 0,
      fill: -1, full: 0, level: 0, step: 0, bloom: 0,
      atRest: false, vis: 1, leaving: 0, laden: false,
      curX: CURRENT_X, curY: CURRENT_Y, seed: 0,
      quads: first.quads, segs: first.segs, rings: first.rings, sprite,
    };
  }

  /** The hulls whose laden ring the look draws itself (`setLadenSuppressed`). */
  get ladenSuppressed(): ReadonlySet<number> { return this.ladenOff; }
  /** The deposits whose mark the look draws itself (`setFieldSuppressed`). */
  get drawnFields(): ReadonlySet<number> { return this.fieldsOff; }

  /** How many cuts stand. The stats line reads it. */
  get cutCount(): number { return this.state.count; }

  /** The canvas half of the look (R814): the one `gather` field. */
  setLook(look: SceneLook): void { this.look = look; }

  /** The `#gatherlook=<id>` knob (R925's pattern): one look id every cut
   *  wears instead of the theme's. '' is no override, and it is no setting. */
  setForced(id: string): void { this.forced = id; }

  /**
   * The `#gather=0` knob: 0 takes the three passes off. It is a measurement
   * and eye-test knob and NOT a viewer setting, the way `#shots` is (R914).
   */
  setEnabled(on: boolean): void {
    this.on = on;
    if (!on) this.clear();
  }

  /** True while the layer draws. */
  get enabled(): boolean { return this.on; }

  /** Sets the visibility of all three passes at once. */
  setVisible(v: boolean): void {
    for (const p of this.passes) p.setVisible(v);
  }

  /** Takes every cut off the water (a season roll, a seek, a hello). */
  clear(): void {
    this.state.clear();
    this.ladenOff.clear();
    this.fieldsOff.clear();
  }

  /**
   * The one advance of the frame.
   *
   * Every pass calls it and the stamp keeps it to one run, because
   * `frame.ts` skips the update of a HIDDEN layer and the first pass may
   * therefore not be the first one to run.
   */
  advance(frame: FrameInfo): void {
    if (this.stamp === frame.now) return;
    this.stamp = frame.now;
    this.t += frame.dtMs / 1000;
    this.ladenOff.clear();
    this.fieldsOff.clear();
    this.leadBySite.clear();
    if (!this.on) {
      this.state.clear();
      return;
    }
    // The pick: the knob's, else the theme's.
    this.resolved = gatherLook(this.forced || this.look.gather);
    const pad = GATHER_LIMITS.cullPadPx;
    this.x0 = frame.cull.x0 - pad; this.y0 = frame.cull.y0 - pad;
    this.x1 = frame.cull.x1 + pad; this.y1 = frame.cull.y1 + pad;
    this.zoom = zoomOf(frame.cam.zoom);
    this.drawing = !hiddenDocument();

    const state = this.state;
    state.begin(frame.dtMs);
    this.feeds.hulls(this.noteHull);
    state.end();

    // One walk over every live cut. It names who speaks for each field
    // (R1124), which every look asks whatever the pick is, and it fills the
    // two suppression sets: the pick is one look for the whole map, so the
    // switches are the look's and the MEMBERS are the live cuts.
    const r = this.resolved.recipe;
    const cuts = state.cuts();
    for (let i = 0; i < state.count; i++) {
      const c = cuts[i];
      const lead = this.leadBySite.get(c.siteId);
      if (lead === undefined || c.id < lead) this.leadBySite.set(c.siteId, c.id);
      if (r.ownsLadenRing) this.ladenOff.add(c.id);
      if (r.drawsField && c.siteId !== 0) this.fieldsOff.add(c.siteId);
    }
  }

  /** The hull walk of `advance`, bound once so a frame allocates no closure. */
  private readonly noteHull = (h: GatherHull): void => {
    // A hull that is not on a GATHER mission needs no field lookup, and the
    // lookup is the one walk of this layer that is not free.
    const f = h.gathering ? this.feeds.field(h.x, h.y, GATHER_LIMITS.fieldSnapPx) : null;
    this.state.note(h, f);
  };

  /** Packs one pass's hook over every live cut, after `advance`. */
  pack(pass: GatherPass): void {
    if (!this.on || !this.drawing) return;
    const fn: GatherHook | undefined = this.resolved.recipe[pass.hook];
    if (fn === undefined) return;
    const c = this.ctx;
    // The context points at the SINKS OF THIS PASS, so a hook writes into the
    // batch its own slot flushes.
    c.quads = pass.quads;
    c.segs = pass.segs;
    c.rings = pass.rings;
    const cuts = this.state.cuts();
    c.t = this.t;
    c.z = this.zoom;
    c.row = this.resolved.row;
    // The deposit mark is one pick for the whole map, so it is written once a
    // pass and not once a cut (R1124).
    c.deposit = this.look.deposit;
    for (let i = 0; i < this.state.count; i++) {
      const cut = cuts[i];
      // The field and the hull both throw marks, so the cull takes the pair.
      if (outside(cut.x, cut.y, this.x0, this.y0, this.x1, this.y1)
        && outside(cut.fx, cut.fy, this.x0, this.y0, this.x1, this.y1)) continue;
      this.fill(c, cut);
      fn(c);
    }
  }

  /** Writes one cut into the reused context. */
  private fill(c: GatherCtx, cut: GatherCut): void {
    const ink = this.inkOf(cut.resource);
    c.alpha = cut.vis ? 1 : 0;
    c.fx = cut.fx; c.fy = cut.fy;
    c.hx = cut.x; c.hy = cut.y;
    // A hull that cuts holds its heading, so the two agree until it turns for
    // home; a piece that stands on the FIELD reads `restHead`.
    c.head = cut.head;
    c.restHead = cut.head;
    c.res = ink.res; c.resHi = ink.hi; c.chips = ink.chips;
    c.ink = cut.ink;
    c.work = cut.work; c.rate = cut.rate; c.shaft = cut.shaft;
    c.tw = cut.tw; c.lot = cut.lot; c.lotN = cut.lotN;
    c.fill = cut.fill; c.full = cut.full;
    c.level = cut.level; c.step = cut.step; c.bloom = cut.bloom;
    c.atRest = cut.atRest; c.vis = cut.vis ? 1 : 0; c.leaving = cut.leaving;
    c.laden = cut.laden;
    c.fieldLead = this.leadBySite.get(cut.siteId) === cut.id;
    c.seed = cut.id;
  }

  /** The commodity's ink, its lighter twin and its chip count, built once. */
  private inkOf(resource: number): { res: number; hi: number; chips: number } {
    let row = this.inks.get(resource);
    if (row === undefined) {
      const res = depositInk(resource);
      row = { res, hi: lighten(res), chips: depositChips(resource) };
      this.inks.set(resource, row);
    }
    return row;
  }
}

/**
 * ONE PASS: one hook of the look, in one slot.
 *
 * It holds no state of its own. The controller owns the clock, the tracker
 * and the context; the pass owns its own three sinks, because the engine runs
 * every `update` before any `draw` and three passes over one triple would
 * each wipe the one before them.
 */
export class GatherPass implements Layer {
  readonly name: string;
  private shown = true;

  constructor(
    /**
     * The controller. It is PUBLIC because the engine's slot table holds
     * passes and not controllers, so a reader reaches the tracker through
     * `engine.layerNamed('gatherunder')`. A replay film hides the three
     * slots (R2402): a replay records no cut.
     */
    readonly owner: GatherLayers,
    readonly hook: HookName,
    readonly quads: QuadSink,
    readonly segs: SegmentSink,
    readonly rings: CircleSink,
  ) {
    this.name = GATHER_SLOTS[hook];
  }

  get visible(): boolean { return this.shown; }
  setVisible(v: boolean): void { this.shown = v; }

  update(frame: FrameInfo): void {
    this.owner.advance(frame);
    this.quads.begin();
    this.segs.begin();
    this.rings.begin();
    this.owner.pack(this);
  }

  draw(ctx: DrawCtx): void {
    // The rings and the lines first, the sprites over them: a glow that a
    // loop crossed must read as the top of the mark.
    this.rings.flush(ctx.cam);
    this.segs.flush(ctx.cam);
    this.quads.flush(ctx.cam);
  }
}
